import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import axios from "axios";
import { Shield, Eye, EyeOff, Check, X } from "lucide-react";
import { API } from "@/api";
import { getPortalToken } from "@/lib/portalSession";


export default function PortalForcePasswordChange() {
  const { portalUser, clearMustChangePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const checks = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
    different: newPassword !== oldPassword && newPassword.length > 0,
  };

  const allValid = Object.values(checks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setLoading(true);
    try {
      const token = getPortalToken();
      const res = await axios.post(`${API}/portal/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Mot de passe modifie avec succes");
      clearMustChangePassword(res.data.access_token);
      navigate("/portal/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || JSON.stringify(e)).join("; "));
      } else {
        toast.error("Erreur lors du changement de mot de passe");
      }
    } finally {
      setLoading(false);
    }
  };

  const CheckItem = ({ ok, label }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-green-600" : "text-gray-400"}`}>
      {ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
      {label}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2d5016] to-[#4a7c2e] px-4 sm:px-0" data-testid="force-password-change">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-6 text-white text-center">
          <Shield className="w-10 h-10 mx-auto mb-2" />
          <h1 className="text-xl font-bold">Changement de mot de passe requis</h1>
          <p className="text-sm text-amber-100 mt-1">
            Pour votre securite, veuillez choisir un nouveau mot de passe.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {portalUser?.name && (
            <p className="text-sm text-gray-500 text-center">
              Bienvenue, <strong>{portalUser.name}</strong>
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
            <div className="relative">
              <input
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 pr-10"
                data-testid="force-old-password"
                autoFocus
              />
              <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 pr-10"
                data-testid="force-new-password"
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              data-testid="force-confirm-password"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <p className="text-xs font-medium text-gray-500 mb-2">Exigences du mot de passe :</p>
            <CheckItem ok={checks.length} label="Au moins 8 caracteres" />
            <CheckItem ok={checks.upper} label="Au moins une majuscule" />
            <CheckItem ok={checks.lower} label="Au moins une minuscule" />
            <CheckItem ok={checks.digit} label="Au moins un chiffre" />
            <CheckItem ok={checks.different} label="Different de l'ancien" />
            <CheckItem ok={checks.match} label="Confirmation identique" />
          </div>

          <button
            type="submit"
            disabled={!allValid || loading}
            className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="force-change-submit"
          >
            {loading ? "Modification..." : "Changer le mot de passe"}
          </button>

          <button type="button" onClick={logout} className="w-full text-sm text-gray-500 hover:text-red-600 transition">
            Se deconnecter
          </button>
        </form>
      </div>
    </div>
  );
}
